import { SessionType } from '@prisma/client'
import { useRef } from 'react'
import { Link, Form, useLoaderData } from 'react-router'
import { type loader } from '#app/root.tsx'
import { ThemeSwitch } from '#app/routes/resources+/theme-switch.tsx'
import { useFocusMode } from '#app/utils/focus-mode-context.tsx'
import { useUser } from '#app/utils/user.ts'
import { Button } from './ui/button'
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuPortal,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
} from './ui/dropdown-menu'
import { Icon } from './ui/icon'

export function UserDropdown() {
	const user = useUser()
	const data = useLoaderData<typeof loader>()
	const formRef = useRef<HTMLFormElement>(null)
	const { mode } = useFocusMode()
	const isLightMode = mode === SessionType.ambient
	const isDarkMode = mode === SessionType.focus
	
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button 
					variant="outline" 
					size="icon" 
					className={`rounded-full backdrop-blur transition-all duration-[3000ms] ${
						isDarkMode
							? 'border-white/30 bg-black/10 hover:bg-black/20'
							: isLightMode
								? 'border-black/30 bg-white/10 hover:bg-white/20'
								: 'border-black/30 dark:border-white/30 bg-white/10 dark:bg-black/10 hover:bg-white/20 dark:hover:bg-black/20'
					}`}
				>
					<Icon name="menu" className={`h-5 w-5 transition-colors duration-[3000ms] ${
						isDarkMode
							? 'text-white/60'
							: isLightMode
								? 'text-black/60'
								: 'text-black/60 dark:text-white/60'
					}`} />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownMenuContent sideOffset={8} align="end">
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to={`/users/${user.username}`}>
							<Icon className="text-body-md" name="avatar">
								Profile
							</Icon>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to={`/users/${user.username}/notes`}>
							<Icon className="text-body-md" name="pencil-2">
								Notes
							</Icon>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to="/settings/profile">
							<Icon className="text-body-md" name="settings">
								Settings
							</Icon>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to="/analytics">
							<Icon className="text-body-md" name="file-text">
								Analytics
							</Icon>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to="/schedule">
							<Icon className="text-body-md" name="clock">
								Schedule
							</Icon>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild>
						<a href="https://freedom.to/dashboard" target="_blank" rel="noopener noreferrer">
							<Icon className="text-body-md" name="arrow-right">
								Freedom Dashboard
							</Icon>
						</a>
					</DropdownMenuItem>
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to="/pricing">
							<Icon className="text-body-md" name="credit-card">
								Subscription
							</Icon>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuSeparator />
					<div className="w-full p-1">
						<ThemeSwitch userPreference={data.requestInfo.userPrefs.theme} />
					</div>
					<DropdownMenuSeparator />
					<Form action="/logout" method="POST" ref={formRef}>
						<DropdownMenuItem asChild>
							<button type="submit" className="w-full">
								<Icon className="text-body-md" name="exit">
									Logout
								</Icon>
							</button>
						</DropdownMenuItem>
					</Form>
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>
	)
}
